/**
 * Spec 086 — shared helpers for the Wiedervorlage surface: "overdue" / grouping (E5) and
 * error-code → i18n mapping for the 085 ProblemDetails contract.
 */
import type { Followup } from './api/followups';

/** Today's date as YYYY-MM-DD in the *local* browser timezone (E5 — not UTC). */
export function todayIsoLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** E5: overdue = dueDate < today (local). Equal to today is NOT overdue (S4). */
export function isFollowupOverdue(dueDate: string): boolean {
  return dueDate < todayIsoLocal();
}

export type FollowupBucket = 'overdue' | 'this-week' | 'later';

/**
 * E5: Überfällig (< today), Diese Woche (today … today+6), Später (> today+6).
 * Boundaries are inclusive equality cases (S4/S5).
 */
export function followupBucket(dueDate: string): FollowupBucket {
  const today = todayIsoLocal();
  if (dueDate < today) return 'overdue';
  if (dueDate <= addDaysIso(today, 6)) return 'this-week';
  return 'later';
}

export interface FollowupGroups {
  overdue: Followup[];
  thisWeek: Followup[];
  later: Followup[];
}

function byDueDateAsc(a: Followup, b: Followup): number {
  return a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0;
}

/** S7: group order Überfällig, Diese Woche, Später; ascending dueDate within each group. */
export function groupFollowups(items: Followup[]): FollowupGroups {
  const groups: FollowupGroups = { overdue: [], thisWeek: [], later: [] };
  for (const item of items) {
    const bucket = followupBucket(item.dueDate);
    if (bucket === 'overdue') groups.overdue.push(item);
    else if (bucket === 'this-week') groups.thisWeek.push(item);
    else groups.later.push(item);
  }
  groups.overdue.sort(byDueDateAsc);
  groups.thisWeek.sort(byDueDateAsc);
  groups.later.sort(byDueDateAsc);
  return groups;
}

/** RFC 7807 `errorCode` (085) → i18n key. Falls back to a generic message (E10/S18). */
const FOLLOWUP_ERROR_I18N_KEYS: Record<string, string> = {
  'followup.active_exists': 'pages.followups.error_active_exists',
  'followup.already_completed': 'pages.followups.error_already_completed',
  'followup.not_completed': 'pages.followups.error_not_completed',
  'followup.not_open': 'pages.followups.error_not_open',
  'followup.subject_required': 'pages.followups.error_subject_required',
  'followup.subject_too_long': 'pages.followups.error_subject_too_long',
  'followup.note_too_long': 'pages.followups.error_note_too_long',
  'followup.due_date_required': 'pages.followups.error_due_date_required',
  'followup.not_found': 'pages.followups.error_not_found',
  'followup.document_required': 'pages.followups.error_document_required',
  // Spec 087 (#486) E11/E12/S20: no open deadline suggestion (already decided, or reprocessed away).
  'deadline_suggestion.not_open': 'pages.followups.error_deadline_suggestion_not_open',
};

export function followupErrorMessage(
  errorCode: string | undefined,
  t: (key: string) => string,
): string {
  const key = errorCode ? FOLLOWUP_ERROR_I18N_KEYS[errorCode] : undefined;
  return t(key ?? 'pages.followups.error_generic');
}

/**
 * Spec 087 (#486) E15/S31: the deadline-suggestion accept/dismiss calls suppress the global error
 * handler (same reasoning as `followupsApi.complete/reopen`) so the review card can show the
 * translated message inline instead of a toast.
 *
 * Review F15: an `errorCode` — even one this map doesn't recognize — always maps to the generic
 * translated message, never the raw (potentially English, potentially technical) `err.message`.
 * The raw message is a fallback ONLY for errors that aren't a server ApiError at all (e.g. a
 * network failure with no errorCode), where there is no translated alternative to offer.
 */
export function deadlineSuggestionErrorMessage(err: unknown, t: (key: string) => string): string {
  const errorCode = (err as { errorCode?: string } | null)?.errorCode;
  if (errorCode) {
    return t(FOLLOWUP_ERROR_I18N_KEYS[errorCode] ?? 'pages.followups.error_generic');
  }
  return err instanceof Error ? err.message : t('pages.followups.error_generic');
}
